import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { useIntl } from "react-intl";

import { CalendarMonth as CalendarIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, IconButton, Stack, styled } from "@mui/material";
import {
  DateOrTimeView,
  DateTimeField,
  DateTimePickerToolbar,
  DateTimePickerToolbarProps,
  PickersLayoutContentWrapper,
  PickersLayoutProps,
  PickersLayoutRoot,
  pickersLayoutClasses,
  usePickerLayout,
} from "@mui/x-date-pickers";
import { localeMap } from "lang";
import { useState } from "react";

const StyledMobileDateTimePicker = styled(MobileDateTimePicker)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.info.main,
  "& label.Mui-focused": {
    color: theme.palette.info.main,
  },
  "& label.Mui-error": {
    color: theme.palette.error.main,
  },
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: theme.palette.info.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.info.main,
    },
  },
  "& .MuiOutlinedInput-root.Mui-error": {
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.error.main,
    },
  },
}));

const StyledMobileDateTimePickerReadOnly = styled(MobileDateTimePicker)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.common.white,
  "& label.Mui-focused": {
    color: theme.palette.secondary.dark,
  },
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": {
      borderColor: theme.palette.secondary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.secondary.main,
      borderWidth: 1,
    },
  },
  userSelect: "none",
  pointerEvents: "none",
}));

interface DateTimePickerProps {
  onChange: (date: Date | null) => void | (() => void);
  label?: string;
  value: Date | null;
  disabled?: boolean;
  disableFuture?: boolean;
  required?: boolean;
  clearable?: boolean;
  id?: string;
  name?: string;
  margin?: "none" | "dense" | "normal";
  placeholder?: string;
  error?: string;
  readOnly?: boolean;
  size?: "small" | "medium" | undefined;
  orientation?: "portrait" | "landscape";
}

export const AsolviDateTimePicker = (props: DateTimePickerProps) => {
  const intl = useIntl();

  const LayoutWithKeyboardView = (pickerProps: PickersLayoutProps<any, Date, DateOrTimeView>) => {
    const { value, onChange } = pickerProps;
    const [showKeyboardView, setShowKeyboardView] = useState(false);

    const { toolbar, tabs, content, actionBar } = usePickerLayout({
      ...pickerProps,
      slotProps: {
        ...pickerProps.slotProps,
        toolbar: {
          ...pickerProps.slotProps?.toolbar,
          // @ts-ignore
          showKeyboardView,
          setShowKeyboardView,
        },
      },
    });

    return (
      <PickersLayoutRoot ownerState={pickerProps}>
        {toolbar}
        {actionBar}
        <PickersLayoutContentWrapper className={pickersLayoutClasses.contentWrapper}>
          {tabs}
          {showKeyboardView ? (
            <Box sx={{ mx: 3, my: 2, width: 272 }}>
              <DateTimeField
                value={value}
                onChange={onChange}
                sx={{ width: "100%" }}
                format={`${localeMap(intl.locale).format} HH:mm`}
                ampm={localeMap(intl.locale).ampm}
                disableFuture={props.disableFuture}
                data-testid="AsolviDateTimePicker-KeyboardTextField"
              />
            </Box>
          ) : (
            content
          )}
        </PickersLayoutContentWrapper>
      </PickersLayoutRoot>
    );
  };

  const ToolbarWithKeyboardViewSwitch = (
    props: DateTimePickerToolbarProps<any> & {
      showKeyboardView?: boolean;
      setShowKeyboardView?: React.Dispatch<React.SetStateAction<boolean>>;
    }
  ) => {
    const { showKeyboardView, setShowKeyboardView, ...other } = props;

    return (
      <Stack
        spacing={2}
        direction={other.isLandscape ? "column" : "row"}
        alignItems="center"
        sx={
          other.isLandscape
            ? {
                gridColumn: 1,
                gridRow: "1 / 3",
              }
            : { gridColumn: "1 / 4", gridRow: 1, mr: 1 }
        }
      >
        <DateTimePickerToolbar {...other} sx={{ flex: "1 1 100%" }} />
        <IconButton
          color="inherit"
          onClick={() => setShowKeyboardView!((prev) => !prev)}
          data-testid="AsolviDateTimePicker-KeyboardViewSwitch"
        >
          {showKeyboardView ? <CalendarIcon /> : <EditIcon />}
        </IconButton>
      </Stack>
    );
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={localeMap(intl.locale).locale}
    >
      {props.disabled || props.readOnly ? (
        <StyledMobileDateTimePickerReadOnly
          label={props.label}
          value={props.value}
          format={`${localeMap(intl.locale).format} HH:mm`}
          ampm={localeMap(intl.locale).ampm}
        />
      ) : (
        <StyledMobileDateTimePicker
          localeText={{
            cancelButtonLabel: intl.formatMessage({
              id: "general.cancel",
              defaultMessage: "Cancel",
            }),
            todayButtonLabel: intl.formatMessage({
              id: "date.today",
              defaultMessage: "Today",
            }),
            okButtonLabel: intl.formatMessage({
              id: "general.ok",
              defaultMessage: "Ok",
            }),
          }}
          disableFuture={props.disableFuture}
          label={props.label}
          value={props.value}
          orientation={props.orientation}
          onAccept={(date: any) => {
            props.onChange(date as Date);
          }}
          ampm={localeMap(intl.locale).ampm}
          closeOnSelect={false}
          format={`${localeMap(intl.locale).format} HH:mm`}
          slots={{
            layout: LayoutWithKeyboardView,
            toolbar: ToolbarWithKeyboardViewSwitch,
          }}
          slotProps={{
            actionBar: {
              actions: props.clearable
                ? ["clear", "today", "cancel", "accept"]
                : ["today", "cancel", "accept"],
            },
            textField: {
              id: props.id,
              name: props.name,
              variant: "outlined",
              required: props.required,
              fullWidth: true,
              error: !!props.error,
              helperText: props.error,
              size: props.size,
            },
          }}
        />
      )}
    </LocalizationProvider>
  );
};
